import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useDevMode } from "@/context/DevModeContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, LogOut, User as UserIcon, Image as ImageIcon,
  LayoutGrid, Coins, Crown, ChevronDown, TrendingUp, TrendingDown, RotateCcw, ArrowRight, Gift, Copy, Check, Flame, Film, Scissors, Bell, X, Sparkle, QrCode, Terminal
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CHANGELOG } from "@/lib/changelog";

const StripeModePill = () => {
  const [mode, setMode] = useState(null);
  useEffect(() => {
    api.get("/stripe/mode").then((r) => setMode(r.data.mode)).catch(() => {});
  }, []);
  if (!mode) return null;
  const isLive = mode === "live";
  return (
    <span
      data-testid="stripe-mode-pill"
      title={`Stripe is running in ${mode.toUpperCase()} mode`}
      className={`hidden sm:inline-flex items-center gap-1 font-mono-hy text-[9px] tracking-widest px-2 py-1 rounded-full border ${
        isLive
          ? "border-[#00E5FF]/40 bg-[#00E5FF]/10 text-[#00E5FF]"
          : "border-[#EB5757]/40 bg-[#EB5757]/10 text-[#EB5757]"
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isLive ? "bg-[#00E5FF] animate-pulse" : "bg-[#EB5757]"}`} />
      {isLive ? "LIVE" : mode.toUpperCase()}
    </span>
  );
};

const NavItem = ({ to, children, testId, active }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`font-mono-hy text-xs px-3 py-2 rounded-full transition-all duration-300 ${
      active ? "text-[#0A0A0A] bg-[#F2C94C]" : "text-white hover:text-[#F2C94C]"
    }`}
  >
    {children}
  </Link>
);

/**
 * ProfileMenu — avatar dropdown replacing the old inline avatar+logout pair.
 *   · Every user gets: Profile · Sign out.
 *   · Admin (is_admin) gets: Profile · Dev Mode toggle · Admin Feedback · Sign out.
 * The Dev Mode toggle here is the ONLY entrypoint that unlocks the extra
 * Dev UI (panel, dev-presets grid). Non-admins never see it under any
 * circumstance — the menu items themselves are gated on `isAdmin`.
 */
function ProfileMenu({ user, isAdmin, devMode, setDevMode, logout, navigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="nav-user-avatar"
        aria-haspopup="menu"
        aria-expanded={open}
        className={`w-9 h-9 rounded-full overflow-hidden border transition-all ${
          isAdmin
            ? "border-[#F2C94C] shadow-[0_0_14px_-4px_rgba(242,201,76,0.7)]"
            : "border-[#F2C94C]/30 hover:border-[#F2C94C]"
        }`}
      >
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[#1A1520] flex items-center justify-center">
            <UserIcon className="w-4 h-4 text-[#F2C94C]" />
          </div>
        )}
      </button>
      {open && (
        <div
          data-testid="nav-profile-menu"
          role="menu"
          className={`absolute right-0 top-11 w-64 rounded-2xl bg-[#120E15] border shadow-[0_20px_60px_-15px_rgba(0,0,0,0.9)] z-50 overflow-hidden ${
            isAdmin ? "border-[#F2C94C]/40" : "border-[#F2C94C]/20"
          }`}
        >
          {/* Header — email + admin badge */}
          <div className="px-4 py-3 border-b border-white/[0.06]">
            <div className="flex items-center gap-2">
              <span className="font-mono-hy text-[10px] text-[#F2C94C] tracking-widest truncate flex-1">{user.email}</span>
              {isAdmin && (
                <span data-testid="nav-admin-badge" className="font-mono-hy text-[8px] px-1.5 py-0.5 rounded bg-[#F2C94C] text-[#0A0A0A] tracking-widest flex-shrink-0">
                  ADMIN
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => { setOpen(false); navigate("/profile"); }}
            data-testid="nav-menu-profile"
            className="w-full text-left px-4 py-2.5 text-white hover:bg-white/[0.04] font-body text-sm flex items-center gap-2"
          >
            <UserIcon className="w-4 h-4 text-[#F2C94C]" /> Profile
          </button>
          {isAdmin && (
            <>
              {/* Gold-bordered Dev Mode toggle — the ONLY entrypoint to the Dev Panel */}
              <div className="mx-2 my-2 p-2.5 rounded-xl border border-[#F2C94C]/50 bg-gradient-to-br from-[#F2C94C]/[0.08] to-transparent">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Terminal className="w-3.5 h-3.5 text-[#F2C94C] flex-shrink-0" />
                    <span className="font-mono-hy text-[10px] text-[#F2C94C] tracking-[0.2em]">DEV MODE</span>
                  </div>
                  <button
                    onClick={() => setDevMode(!devMode)}
                    data-testid="nav-devmode-toggle"
                    aria-pressed={devMode}
                    className={`relative w-11 h-5 rounded-full border transition-all ${
                      devMode
                        ? "border-[#F2C94C] bg-[#F2C94C]/25 shadow-[0_0_12px_-2px_rgba(242,201,76,0.7)]"
                        : "border-white/20 bg-[#0A0A0A]"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-3.5 h-3.5 rounded-full transition-all ${
                        devMode ? "left-[calc(100%-1.125rem)] bg-[#F2C94C]" : "left-0.5 bg-white/30"
                      }`}
                    />
                  </button>
                </div>
                <div className="font-body text-[10px] text-white/60 mt-1.5 leading-snug">
                  {devMode ? "Dev panel + dev presets are visible." : "Regular user view."}
                </div>
              </div>
              <button
                onClick={() => { setOpen(false); navigate("/admin/feedback"); }}
                data-testid="nav-menu-admin-feedback"
                className="w-full text-left px-4 py-2.5 text-white hover:bg-white/[0.04] font-body text-sm flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4 text-[#00E5FF]" /> Admin · Feedback
              </button>
            </>
          )}
          <div className="border-t border-white/[0.06]" />
          <button
            onClick={() => { setOpen(false); logout(); }}
            data-testid="nav-logout-btn"
            className="w-full text-left px-4 py-2.5 text-white hover:bg-white/[0.04] hover:text-[#E27D60] font-body text-sm flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

const REASON_LABEL = {
  signup_grant: "Signup bonus",
  "spend:image": "Image create",
  "spend:edit": "Image edit",
  "spend:turnaround": "Turnaround sheet",
  "spend:video": "Animate",
  "spend:text-video": "Text → Video",
  "spend:video-to-video": "Video → Video",
  "spend:turntable": "Turntable spin",
  "spend:tts": "Voice-over",
  "spend:bio-reroll": "Bio reroll",
  "refund:video": "Refund · Animate",
  "refund:text-video": "Refund · Text → Video",
  "refund:video-to-video": "Refund · Video → Video",
  "refund:turntable": "Refund · Turntable",
  "purchase:credits_100":  "Starter Pack · 100 credits",
  "purchase:credits_300":  "Standard Pack · 300 credits",
  "purchase:credits_600":  "Power Pack · 600 credits",
  "purchase:credits_500":  "Pack · 500 credits",
  "purchase:credits_2000": "Pack · 2000 credits",
  "purchase:creator_monthly": "Subscription · Creator",
  "purchase:studio_monthly": "Subscription · Studio",
  "renewal:creator_monthly": "Renewal · Creator",
  "renewal:studio_monthly": "Renewal · Studio",
  "referral:friend_activated": "Friend activated (+25)",
  "streak_bonus": "Streak bonus (+5)",
};

const timeAgo = (iso) => {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
};

export default function Navbar() {
  const { user, logout, checkAuth } = useAuth();
  const { isAdmin, devMode, setDevMode, isFeatureEnabled } = useDevMode();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [referral, setReferral] = useState(null);
  const [streak, setStreak] = useState(null);
  const [copied, setCopied] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsOpen, setJobsOpen] = useState(false);
  const [notifiedJobIds] = useState(() => {
    try {
      const raw = localStorage.getItem("geneo_notified_jobs");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });
  const popoverRef = useRef(null);
  const jobsRef = useRef(null);
  const [betaDismissed, setBetaDismissed] = useState(() => {
    try { return localStorage.getItem("geneo_beta_dismissed_v1") === "1"; }
    catch { return false; }
  });
  const dismissBeta = () => {
    try { localStorage.setItem("geneo_beta_dismissed_v1", "1"); } catch {}
    setBetaDismissed(true);
  };

  const persistNotified = (id) => {
    notifiedJobIds.add(id);
    try {
      // Cap stored set at 200 to avoid indefinite growth
      const arr = Array.from(notifiedJobIds).slice(-200);
      localStorage.setItem("geneo_notified_jobs", JSON.stringify(arr));
    } catch {}
  };

  const credits = user?.credits ?? 0;
  const isSubscriber = !!user?.subscription;
  const lowCredits = user && credits <= 10;

  // Background jobs — poll every 12s while user is signed in
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchJobs = async () => {
      try {
        const { data } = await api.get("/jobs");
        if (cancelled) return;
        setJobs(data || []);
        // Notify on newly completed jobs (once each, persisted across reloads)
        for (const j of data || []) {
          if (j.status === "done" && !notifiedJobIds.has(j.job_id)) {
            persistNotified(j.job_id);
            toast.success(`${j.type.replace(/-/g, " ")} finished — tap the bell to view`, { duration: 6000 });
          } else if (j.status === "failed" && !notifiedJobIds.has(j.job_id)) {
            persistNotified(j.job_id);
            toast.error(`${j.type.replace(/-/g, " ")} failed — credits refunded`, { duration: 5000 });
          }
        }
      } catch {}
    };
    fetchJobs();
    const iv = setInterval(fetchJobs, 12000);
    return () => { cancelled = true; clearInterval(iv); };
    // persistNotified is a stable inline helper closed over `notifiedJobIds`;
    // depending on it directly would create a new function each render and
    // reset the 12s polling interval. Leaving deps at [user, notifiedJobIds].
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, notifiedJobIds]);

  // Close jobs tray on outside click
  useEffect(() => {
    if (!jobsOpen) return;
    const onClick = (e) => { if (jobsRef.current && !jobsRef.current.contains(e.target)) setJobsOpen(false); };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [jobsOpen]);

  const dismissJob = async (jobId) => {
    try {
      await api.delete(`/jobs/${jobId}`);
      setJobs((prev) => prev.filter((j) => j.job_id !== jobId));
    } catch {}
  };

  const startLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/studio";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Global refund listener — refresh credits + celebrate
  useEffect(() => {
    const onRefund = (e) => {
      const { refunded } = e.detail || {};
      toast.success(`Credits refunded — ${refunded} credits are back in your vault.`, { duration: 5000 });
      checkAuth();
      // Prefetch new ledger for the drawer if open
      if (open) fetchLedger();
    };
    window.addEventListener("geneo:credits-refunded", onRefund);
    return () => window.removeEventListener("geneo:credits-refunded", onRefund);
    // Note: fetchLedger is intentionally excluded (redefined each render).
  }, [open, checkAuth]);

  // Close popover on outside click / ESC
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("mousedown", onClick); window.removeEventListener("keydown", onKey); };
  }, [open]);

  const fetchLedger = async () => {
    setLoadingLedger(true);
    try {
      const { data } = await api.get("/me/credits");
      setLedger((data.recent || []).slice(0, 10));
    } catch {
      setLedger([]);
    } finally {
      setLoadingLedger(false);
    }
  };

  const fetchReferral = async () => {
    try {
      const { data } = await api.get("/me/referral");
      setReferral(data);
    } catch {
      setReferral(null);
    }
  };

  const fetchStreak = async () => {
    try {
      const { data } = await api.get("/me/streak");
      setStreak(data);
    } catch {
      setStreak(null);
    }
  };

  const referralUrl = referral?.referral_code
    ? `${window.location.origin}/studio?ref=${referral.referral_code}`
    : "";

  const copyReferral = async () => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast.success("Referral link copied — share and earn 25 credits per friend.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copy failed — long-press the link to copy manually.");
    }
  };

  const toggleDrawer = () => {
    const next = !open;
    setOpen(next);
    if (next) { fetchLedger(); fetchReferral(); fetchStreak(); }
  };

  return (
    <>
    <nav className="sticky top-0 z-50 glass-panel">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#F2C94C] to-[#E27D60] flex items-center justify-center transition-transform duration-300 group-hover:rotate-12">
            <Sparkles className="w-5 h-5 text-[#0A0A0A]" strokeWidth={2.5} />
          </div>
          <div className="leading-none">
            <div className="font-display text-lg tracking-tight text-white">GENEO AI</div>
            <div className="font-mono-hy text-[9px] text-[#F2C94C]">AFRO SCI-FI · ANIMATION</div>
          </div>
        </Link>
        <StripeModePill />

        <div className="flex items-center gap-2">
          {user && (
            <>
              <NavItem to="/studio" testId="nav-studio" active={pathname.startsWith("/studio")}>
                <ImageIcon className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />Studio
              </NavItem>
              {isFeatureEnabled("gallery") && (
                <NavItem to="/gallery" testId="nav-gallery" active={pathname.startsWith("/gallery")}>
                  <LayoutGrid className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />Gallery
                </NavItem>
              )}
              {isFeatureEnabled("storyboard") && (
                <NavItem to="/storyboard" testId="nav-storyboard" active={pathname.startsWith("/storyboard")}>
                  <Film className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />Storyboard
                </NavItem>
              )}
              {isFeatureEnabled("timeline_editor") && (
                <NavItem to="/editor" testId="nav-editor" active={pathname.startsWith("/editor")}>
                  <Scissors className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />Editor
                </NavItem>
              )}
              {isFeatureEnabled("avatar_studio") && (
                <NavItem to="/avatar" testId="nav-avatar" active={pathname.startsWith("/avatar")}>
                  <Sparkle className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />Avatar
                </NavItem>
              )}
              <NavItem to="/pricing" testId="nav-pricing" active={pathname.startsWith("/pricing")}>
                <Crown className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />Pricing
              </NavItem>
              {isFeatureEnabled("qr_generator") && (
                <NavItem to="/qr-generator" testId="nav-qr" active={pathname.startsWith("/qr-generator")}>
                  <QrCode className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />QR
                </NavItem>
              )}

              {/* Background jobs bell */}
              <div className="relative" ref={jobsRef}>
                <button
                  onClick={() => setJobsOpen((o) => !o)}
                  data-testid="nav-jobs-bell"
                  title="Background renders"
                  className="relative font-mono-hy text-[11px] w-9 h-9 rounded-full flex items-center justify-center border border-[#F2C94C]/25 text-white hover:text-[#F2C94C] hover:border-[#F2C94C] transition-all"
                >
                  <Bell className="w-4 h-4" />
                  {jobs.some((j) => j.status === "running") && (
                    <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#EB5757] text-white text-[8px] font-bold flex items-center justify-center animate-pulse">
                      {jobs.filter((j) => j.status === "running").length}
                    </span>
                  )}
                  {jobs.some((j) => j.status === "done") && !jobs.some((j) => j.status === "running") && (
                    <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#27AE60] text-white text-[8px] font-bold flex items-center justify-center">
                      {jobs.filter((j) => j.status === "done").length}
                    </span>
                  )}
                </button>
                {jobsOpen && (
                  <div
                    data-testid="jobs-tray"
                    className="absolute right-0 top-11 w-80 max-h-96 overflow-y-auto rounded-2xl bg-[#120E15] border border-[#F2C94C]/25 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.9)] z-50 p-3"
                  >
                    <div className="font-mono-hy text-[10px] text-[#F2C94C] mb-2">// Background renders</div>
                    {jobs.length === 0 ? (
                      <div className="font-mono-hy text-[10px] text-white text-center py-6">No jobs in flight.</div>
                    ) : (
                      <div className="space-y-2">
                        {jobs.map((j) => (
                          <div key={j.job_id} data-testid={`job-item-${j.job_id}`} className={`p-3 rounded-xl border ${j.status === "running" ? "border-[#00E5FF]/30 bg-[#00E5FF]/5" : j.status === "done" ? "border-[#27AE60]/30 bg-[#27AE60]/5" : "border-[#E27D60]/30 bg-[#E27D60]/5"}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="font-mono-hy text-[9px] uppercase mb-0.5" style={{ color: j.status === "running" ? "#00E5FF" : j.status === "done" ? "#27AE60" : "#E27D60" }}>
                                  {j.status === "running" ? "RENDERING…" : j.status === "done" ? "READY" : "FAILED"}
                                </div>
                                <div className="font-body text-xs text-white truncate">{(j.meta?.title || j.type).replace(/-/g, " ")}</div>
                                <div className="font-mono-hy text-[9px] text-white">{timeAgo(j.created_at)} · {j.type}</div>
                              </div>
                              {j.status !== "running" && (
                                <button
                                  onClick={() => dismissJob(j.job_id)}
                                  data-testid={`job-dismiss-${j.job_id}`}
                                  className="w-5 h-5 rounded-full text-white hover:text-[#E27D60] flex items-center justify-center"
                                >
                                  <span className="text-[14px] leading-none">×</span>
                                </button>
                              )}
                            </div>
                            {j.status === "done" && j.result?.video_url && (
                              <div className="flex items-center gap-1.5 mt-2">
                                <a
                                  href={j.result.video_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  data-testid={`job-open-${j.job_id}`}
                                  className="inline-flex items-center gap-1 font-mono-hy text-[9px] px-2.5 py-1 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] transition-all"
                                >
                                  <ArrowRight className="w-3 h-3" /> Open MP4
                                </a>
                                {j.result.generation_id && !j.result.is_public && (
                                  <button
                                    onClick={async () => {
                                      try {
                                        await api.patch(`/generations/${j.result.generation_id}/visibility`, { is_public: true });
                                        setJobs((prev) => prev.map((x) => x.job_id === j.job_id ? { ...x, result: { ...x.result, is_public: true } } : x));
                                        toast.success("Reel sent to the community gallery.");
                                      } catch {
                                        toast.error("Publish failed");
                                      }
                                    }}
                                    data-testid={`job-publish-${j.job_id}`}
                                    className="inline-flex items-center gap-1 font-mono-hy text-[9px] px-2.5 py-1 rounded-full border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 transition-all"
                                  >
                                    Send to Gallery
                                  </button>
                                )}
                                {j.result.is_public && (
                                  <span className="inline-flex items-center gap-1 font-mono-hy text-[9px] px-2.5 py-1 rounded-full bg-[#27AE60]/20 border border-[#27AE60]/40 text-[#27AE60]">
                                    In Gallery
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Credit badge + history drawer */}
              <div className="relative" ref={popoverRef}>
                <button
                  onClick={toggleDrawer}
                  data-testid="nav-credit-badge"
                  className={`font-mono-hy text-[11px] px-3 py-2 rounded-full flex items-center gap-1.5 border transition-all hover:-translate-y-0.5 ${
                    lowCredits
                      ? "bg-[#E27D60]/15 border-[#E27D60]/60 text-[#E27D60] hover:bg-[#E27D60]/25"
                      : "bg-[#F2C94C]/10 border-[#F2C94C]/40 text-[#F2C94C] hover:bg-[#F2C94C]/20"
                  }`}
                  title={`${credits} credits — tap for activity`}
                >
                  <Coins className="w-3.5 h-3.5" />
                  <span className="font-bold">{credits}</span>
                  {isSubscriber && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-[#F2C94C] text-[#0A0A0A] font-bold uppercase">PRO</span>}
                  <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>

                {open && (
                  <div
                    data-testid="credit-history-drawer"
                    className="absolute right-0 mt-2 w-80 rounded-2xl bg-[#120E15] border border-[#F2C94C]/25 shadow-[0_20px_60px_-10px_rgba(0,0,0,0.6)] overflow-hidden"
                  >
                    <div className="p-4 flex items-baseline justify-between border-b border-[#F2C94C]/10">
                      <div>
                        <div className="font-mono-hy text-[10px] text-white">// RECENT ACTIVITY</div>
                        <div className="font-display text-2xl text-white font-bold mt-1">{credits} <span className="text-sm text-white font-body">credits</span></div>
                      </div>
                      <button
                        onClick={() => { setOpen(false); navigate("/pricing"); }}
                        data-testid="drawer-topup-btn"
                        className="font-mono-hy text-[10px] px-3 py-2 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] transition-all flex items-center gap-1"
                      >
                        Add credits <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="max-h-72 overflow-y-auto py-1">
                      {loadingLedger && (
                        <div className="p-6 font-mono-hy text-[10px] text-white text-center">loading…</div>
                      )}
                      {!loadingLedger && ledger.length === 0 && (
                        <div className="p-6 font-mono-hy text-[10px] text-white text-center">No activity yet. Generate to spend, top up to earn.</div>
                      )}
                      {!loadingLedger && ledger.map((row, i) => {
                        const positive = row.delta > 0;
                        const isRefund = (row.reason || "").startsWith("refund:");
                        const Icon = positive ? (isRefund ? RotateCcw : TrendingUp) : TrendingDown;
                        const color = positive ? (isRefund ? "#00E5FF" : "#27AE60") : "#E27D60";
                        return (
                          <div
                            key={`${row.at || i}-${row.reason || ""}-${row.delta || 0}`}
                            data-testid={`ledger-row-${i}`}
                            className="px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-white/5"
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${color}22`, border: `1px solid ${color}55` }}>
                                <Icon className="w-3.5 h-3.5" style={{ color }} />
                              </div>
                              <div className="min-w-0">
                                <div className="font-body text-xs text-white truncate">{REASON_LABEL[row.reason] || row.reason}</div>
                                <div className="font-mono-hy text-[9px] text-white">{timeAgo(row.created_at)} ago</div>
                              </div>
                            </div>
                            <div className="font-mono-hy text-xs font-bold whitespace-nowrap" style={{ color }}>
                              {positive ? "+" : ""}{row.delta}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Streak strip */}
                    {streak && (
                      <div
                        className="border-t border-[#F2C94C]/10 px-4 py-3 flex items-center gap-3 bg-[#0A0A0A]/40"
                        data-testid="streak-strip"
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${streak.is_broken ? "bg-[#605C50]/25" : "bg-gradient-to-br from-[#FF6B4A] to-[#E27D60]"}`}>
                          <Flame className={`w-4 h-4 ${streak.is_broken ? "text-white" : "text-[#0A0A0A]"}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono-hy text-[10px] text-[#F2C94C]">
                            {streak.streak_count > 0
                              ? <>DAILY STREAK · <span className="text-[#FF6B4A]" data-testid="streak-count">{streak.streak_count}</span> day{streak.streak_count === 1 ? "" : "s"}</>
                              : "START YOUR STREAK"
                            }
                          </div>
                          <div className="font-body text-[11px] text-white mt-0.5">
                            {streak.is_broken
                              ? "Streak broken — create today to start fresh."
                              : streak.days_until_bonus === 0
                              ? <span className="text-[#F2C94C]">🎉 Just earned +{streak.bonus_credits} bonus credits!</span>
                              : <>Generate <span className="text-white font-semibold">{streak.days_until_bonus}</span> more day{streak.days_until_bonus === 1 ? "" : "s"} to earn <span className="text-[#F2C94C] font-semibold">+{streak.bonus_credits} credits</span></>
                            }
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Referral widget */}
                    {referral && (
                      <div
                        className="border-t border-[#F2C94C]/10 p-4 bg-gradient-to-br from-[#F2C94C]/[0.06] via-transparent to-[#00E5FF]/[0.05]"
                        data-testid="referral-widget"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#F2C94C] to-[#FF6B4A] flex items-center justify-center flex-shrink-0">
                            <Gift className="w-3.5 h-3.5 text-[#0A0A0A]" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-mono-hy text-[10px] text-[#F2C94C]">EARN +10 CREDITS PER SIGNUP</div>
                            <div className="font-body text-[11px] text-white">Share your link. The moment a friend signs up, +10 credits land in your vault.</div>
                          </div>
                        </div>

                        <button
                          onClick={copyReferral}
                          data-testid="referral-copy-btn"
                          className="w-full font-mono-hy text-[10px] px-3 py-2.5 rounded-xl bg-[#0A0A0A] border border-[#F2C94C]/30 hover:border-[#F2C94C] text-white transition-all flex items-center justify-between gap-2 group"
                        >
                          <span className="truncate flex-1 text-left" data-testid="referral-link">
                            {referralUrl.replace(/^https?:\/\//, "")}
                          </span>
                          {copied ? (
                            <Check className="w-3.5 h-3.5 text-[#27AE60] flex-shrink-0" />
                          ) : (
                            <Copy className="w-3.5 h-3.5 text-[#F2C94C] flex-shrink-0 group-hover:scale-110 transition-transform" />
                          )}
                        </button>

                        <div className="mt-3 grid grid-cols-2 gap-2 text-center">
                          <div>
                            <div className="font-display text-base text-[#F2C94C] font-bold" data-testid="referral-activated">{referral.total_referrals ?? referral.activated_friends ?? 0}</div>
                            <div className="font-mono-hy text-[8px] text-white">REFERRED</div>
                          </div>
                          <div>
                            <div className="font-display text-base text-[#27AE60] font-bold" data-testid="referral-earned">+{referral.credits_earned}</div>
                            <div className="font-mono-hy text-[8px] text-white">EARNED</div>
                          </div>
                        </div>

                        <Link
                          to="/profile"
                          data-testid="referral-profile-link"
                          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-white/[0.08] hover:border-[#F2C94C]/50 hover:bg-[#F2C94C]/[0.06] font-mono-hy text-[9px] tracking-[0.18em] text-white transition-all"
                        >
                          VIEW PROFILE & STATS →
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          {!user && (
            <>
              <NavItem to="/pricing" testId="nav-pricing-guest" active={pathname.startsWith("/pricing")}>
                <Crown className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />Pricing
              </NavItem>
              <NavItem to="/qr-generator" testId="nav-qr-guest" active={pathname.startsWith("/qr-generator")}>
                <QrCode className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />QR
              </NavItem>
            </>
          )}
          {!user ? (
            <button
              onClick={startLogin}
              data-testid="nav-signin-btn"
              className="ml-3 font-mono-hy text-xs px-5 py-2.5 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] transition-all duration-300 hover:-translate-y-0.5 glow-gold"
            >
              Enter
            </button>
          ) : (
            <div className="flex items-center gap-3 ml-3">
              <ProfileMenu user={user} isAdmin={isAdmin} devMode={devMode} setDevMode={setDevMode} logout={logout} navigate={navigate} />
            </div>
          )}
        </div>
      </div>
    </nav>
    {/* Beta banner — persistent app-wide notice under the nav; dismissible per-user (localStorage) */}
    {!betaDismissed && (
      <div
        data-testid="beta-banner"
        className="sticky top-[64px] z-40 border-b border-[#F2C94C]/15 bg-[#0A0A0A]/85 backdrop-blur-md"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-1.5 flex items-center justify-center gap-2 text-center relative">
          <span className="w-1.5 h-1.5 rounded-full bg-[#F2C94C] pulse-ring" aria-hidden="true" />
          <span className="font-mono-hy text-[10px] tracking-widest text-[#F2C94C]/90">BETA</span>
          <span className="font-mono-hy text-[10px] text-white">·</span>
          <span className="font-mono-hy text-[10px] text-white">
            Geneo AI is in beta — we are always upgrading for a better experience.
          </span>
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="whats-new-trigger"
                className="font-mono-hy text-[10px] text-[#00E5FF] hover:text-[#F2C94C] flex items-center gap-1 transition-colors"
              >
                <Sparkle className="w-3 h-3" />
                What's new
              </button>
            </PopoverTrigger>
            <PopoverContent
              data-testid="whats-new-popover"
              align="center"
              sideOffset={8}
              className="w-[340px] p-0 bg-[#120E15] border border-[#F2C94C]/30 shadow-[0_16px_40px_-8px_rgba(0,0,0,0.8)]"
            >
              <div className="px-4 py-3 border-b border-[#F2C94C]/15 flex items-center gap-2">
                <Sparkle className="w-3.5 h-3.5 text-[#F2C94C]" />
                <div className="font-mono-hy text-[10px] text-[#F2C94C] tracking-widest">WHAT'S NEW · LAST 3 UPGRADES</div>
              </div>
              <div className="p-2 max-h-[60vh] overflow-y-auto">
                {CHANGELOG.map((item, i) => (
                  <div
                    key={item.id}
                    data-testid={`changelog-item-${item.id}`}
                    className={`p-3 rounded-xl ${i > 0 ? "mt-1" : ""} hover:bg-[#F2C94C]/5 transition-colors`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-mono-hy text-[9px] px-1.5 py-0.5 rounded-full ${
                        item.tag === "New"
                          ? "bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/40"
                          : "bg-[#F2C94C]/15 text-[#F2C94C] border border-[#F2C94C]/40"
                      }`}>
                        {item.tag.toUpperCase()}
                      </span>
                      <span className="font-mono-hy text-[9px] text-white">{item.date}</span>
                    </div>
                    <div className="font-display text-sm text-white">{item.title}</div>
                    <div className="font-body text-xs text-white mt-0.5">{item.body}</div>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <button
            onClick={dismissBeta}
            data-testid="beta-banner-dismiss"
            aria-label="Dismiss beta banner"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full text-white hover:text-[#F2C94C] hover:bg-[#F2C94C]/10 flex items-center justify-center transition-all"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    )}
    </>
  );
}
